import {
  type FuelReconciliation,
  FUEL_LEDGER_STATUS_LABELS,
  RECONCILIATION_CLASSIFICATION_LABELS,
  RECONCILIATION_DATA_QUALITY_LABELS,
  RECONCILIATION_RULE_LABELS,
  RECONCILIATION_STATUS_LABELS,
  reconciliationClassificationSchema,
  reconciliationStatusSchema,
} from "@station/contracts";
import { addDays, Decimal, Money, toIstanbulDate } from "@station/core";
import {
  Alert,
  Button,
  Card,
  DataTable,
  type DataTableColumn,
  DateInput,
  Drawer,
  EmptyState,
  FormField,
  QuantityText,
  Select,
  SkeletonRows,
  StatusBadge,
  Textarea,
  useToast,
} from "@station/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Calculator, Scale } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Field, Section } from "../../../features/expenses/shared.js";
import { cumulativeVarianceChartOption } from "../../../features/fuel/chart-options.js";
import {
  useCumulativeVariance,
  useFuelPermissions,
  useFuelReconciliations,
  useFuelSettings,
  useRefreshFuel,
  useTanks,
} from "../../../features/fuel/queries.js";
import {
  CLASSIFICATION_TONE,
  cumulativeSeries,
  explanationRequired,
  FINDING_SEVERITY_TONE,
  hasLossBreakdown,
  lastCumulative,
  RECONCILIATION_STATUS_TONE,
  reconciliationFormula,
  varianceDirection,
} from "../../../features/fuel/reconciliation-values.js";
import {
  ChartWithTable,
  FuelPage,
  fuelSearchShape,
  NoStations,
  StationField,
  Th,
  useFuelStation,
} from "../../../features/fuel/shared.js";
import { api } from "../../../lib/api.js";
import { useCursorPages } from "../../../lib/cursor-pages.js";
import { errorMessage, isApiError } from "../../../lib/errors.js";
import { formatDate } from "../../../lib/format.js";
import { useStepUp } from "../../../lib/step-up.js";

const date = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined);

const searchSchema = z.object({
  ...fuelSearchShape,
  tank: z.string().uuid().optional().catch(undefined),
  classification: reconciliationClassificationSchema.optional().catch(undefined),
  status: reconciliationStatusSchema.optional().catch(undefined),
  from: date,
  to: date,
  row: z.string().uuid().optional().catch(undefined),
});

export const Route = createFileRoute("/_app/akaryakit/mutabakat")({
  validateSearch: (search) => searchSchema.parse(search),
  component: ReconciliationPage,
});

/** Varsayılan aralık: son 14 gün (dünden geriye; bugünün mutabakatı gün kapanmadan hesaplanamaz). */
function defaultRange(now: Date): { from: string; to: string } {
  const today = toIstanbulDate(now);
  return { from: addDays(today, -14), to: today };
}

function ReconciliationPage(): ReactElement {
  const { t } = useTranslation();
  const permissions = useFuelPermissions();
  return (
    <FuelPage
      title={t("menu.fuelReconciliation")}
      description={t("fuel.reconciliation.description")}
      breadcrumb={[{ label: t("menu.fuelReconciliation") }]}
    >
      {({ companyId }) =>
        permissions.canRead ? (
          <ReconciliationBody companyId={companyId} />
        ) : (
          <Alert tone="info">{t("fuel.noPermission")}</Alert>
        )
      }
    </FuelPage>
  );
}

function ReconciliationBody({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const station = useFuelStation(companyId);
  const tanks = useTanks(station.stationId);
  const range = useMemo(() => defaultRange(new Date()), []);
  const from = search.from ?? range.from;
  const to = search.to ?? range.to;

  const setFilters = (patch: Partial<z.infer<typeof searchSchema>>) =>
    void navigate({
      to: ".",
      search: ((previous: Record<string, unknown>) =>
        Object.fromEntries(Object.entries({ ...previous, ...patch }).filter(([, value]) => value))) as never,
      replace: true,
    });

  const filters = {
    tankId: search.tank,
    from,
    to,
    classification: search.classification,
    status: search.status,
  };
  const pages = useCursorPages(JSON.stringify({ ...filters, station: station.stationId }));
  const list = useFuelReconciliations(station.stationId, filters, pages.cursor);
  const rows = list.data?.items ?? [];
  const selected = rows.find((row) => row.id === search.row) ?? null;

  const columns = useMemo<DataTableColumn<FuelReconciliation>[]>(
    () => [
      {
        id: "businessDate",
        header: t("fuel.reconciliation.fields.day"),
        hideable: false,
        exportValue: (row) => row.businessDate,
        cell: (row) => formatDate(row.businessDate),
      },
      {
        id: "tank",
        header: t("fuel.reconciliation.fields.tank"),
        exportValue: (row) => `${row.tankNo} ${row.productName}`,
        cell: (row) => t("fuel.tanks.tankTitle", { tankNo: row.tankNo, product: row.productName }),
      },
      {
        id: "openingL",
        header: t("fuel.reconciliation.fields.opening"),
        align: "right",
        exportValue: (row) => row.openingL,
        cell: (row) => <QuantityText value={row.openingL} unit="LITRE" />,
      },
      {
        id: "deliveredL",
        header: t("fuel.reconciliation.fields.delivered"),
        align: "right",
        exportValue: (row) => row.deliveredL,
        cell: (row) => <QuantityText value={row.deliveredL} unit="LITRE" />,
      },
      {
        id: "totalizerSalesL",
        header: t("fuel.reconciliation.fields.totalizerSales"),
        align: "right",
        exportValue: (row) => row.totalizerSalesL,
        cell: (row) => <QuantityText value={row.totalizerSalesL} unit="LITRE" />,
      },
      {
        id: "transactionSalesL",
        header: t("fuel.reconciliation.fields.transactionSales"),
        align: "right",
        defaultVisible: false,
        exportValue: (row) => row.transactionSalesL,
        cell: (row) => <QuantityText value={row.transactionSalesL} unit="LITRE" />,
      },
      {
        id: "testReturnedL",
        header: t("fuel.reconciliation.fields.testReturned"),
        align: "right",
        defaultVisible: false,
        exportValue: (row) => row.testReturnedL,
        cell: (row) => <QuantityText value={row.testReturnedL} unit="LITRE" />,
      },
      {
        id: "bookStockL",
        header: t("fuel.reconciliation.fields.book"),
        align: "right",
        exportValue: (row) => row.bookStockL,
        cell: (row) => <QuantityText value={row.bookStockL} unit="LITRE" />,
      },
      {
        id: "closingL",
        header: t("fuel.reconciliation.fields.closing"),
        align: "right",
        exportValue: (row) => row.closingL,
        cell: (row) => <QuantityText value={row.closingL} unit="LITRE" />,
      },
      {
        id: "varianceL",
        header: t("fuel.reconciliation.fields.variance"),
        align: "right",
        exportValue: (row) => row.varianceL,
        cell: (row) => <VarianceCell item={row} />,
      },
      {
        id: "cumulativeVarianceL",
        header: t("fuel.reconciliation.fields.cumulative"),
        align: "right",
        exportValue: (row) => row.cumulativeVarianceL,
        cell: (row) => <QuantityText value={row.cumulativeVarianceL} unit="LITRE" />,
      },
      {
        id: "classification",
        header: t("fuel.reconciliation.fields.classification"),
        exportValue: (row) => RECONCILIATION_CLASSIFICATION_LABELS[row.classification],
        cell: (row) => (
          <StatusBadge tone={CLASSIFICATION_TONE[row.classification]}>
            {RECONCILIATION_CLASSIFICATION_LABELS[row.classification]}
          </StatusBadge>
        ),
      },
      {
        id: "status",
        header: t("common.status"),
        exportValue: (row) => RECONCILIATION_STATUS_LABELS[row.status],
        cell: (row) => (
          <StatusBadge tone={RECONCILIATION_STATUS_TONE[row.status]}>
            {RECONCILIATION_STATUS_LABELS[row.status]}
          </StatusBadge>
        ),
      },
    ],
    [t],
  );

  if (station.error) return <Alert tone="danger">{errorMessage(station.error)}</Alert>;
  if (station.isLoading) return <SkeletonRows rows={4} />;
  if (station.stationId === null) return <NoStations />;

  const tankItems = tanks.data?.items ?? [];

  return (
    <>
      <ComputeDay stationId={station.stationId} />

      <DataTable
        caption={t("fuel.reconciliation.caption")}
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        loading={list.isPending}
        error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            headingLevel={2}
            icon={<Scale />}
            title={t("fuel.reconciliation.emptyTitle")}
            description={t("fuel.reconciliation.emptyDescription")}
          />
        }
        onRowClick={(row) => setFilters({ row: row.id })}
        filters={
          <>
            <StationField selection={station} />
            <FormField label={t("fuel.reconciliation.fields.tank")} className="w-56">
              {(control) => (
                <Select
                  {...control}
                  value={search.tank ?? ""}
                  onChange={(event) => setFilters({ tank: event.target.value || undefined })}
                >
                  <option value="">{t("common.all")}</option>
                  {tankItems.map((tank) => (
                    <option key={tank.tankId} value={tank.tankId}>
                      {t("fuel.tanks.tankTitle", { tankNo: tank.tankNo, product: tank.productName })}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
            <FormField label={t("fuel.reconciliation.fields.classification")} className="w-40">
              {(control) => (
                <Select
                  {...control}
                  value={search.classification ?? ""}
                  onChange={(event) =>
                    setFilters({
                      classification:
                        event.target.value === ""
                          ? undefined
                          : reconciliationClassificationSchema.parse(event.target.value),
                    })
                  }
                >
                  <option value="">{t("common.all")}</option>
                  {reconciliationClassificationSchema.options.map((value) => (
                    <option key={value} value={value}>
                      {RECONCILIATION_CLASSIFICATION_LABELS[value]}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
            <FormField label={t("common.status")} className="w-36">
              {(control) => (
                <Select
                  {...control}
                  value={search.status ?? ""}
                  onChange={(event) =>
                    setFilters({
                      status:
                        event.target.value === ""
                          ? undefined
                          : reconciliationStatusSchema.parse(event.target.value),
                    })
                  }
                >
                  <option value="">{t("common.all")}</option>
                  {reconciliationStatusSchema.options.map((value) => (
                    <option key={value} value={value}>
                      {RECONCILIATION_STATUS_LABELS[value]}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
            <FormField label={t("fuel.reconciliation.from")} className="w-40">
              {(control) => (
                <DateInput
                  {...control}
                  value={from}
                  onValueChange={(value) => setFilters({ from: value ?? undefined })}
                />
              )}
            </FormField>
            <FormField label={t("fuel.reconciliation.to")} className="w-40">
              {(control) => (
                <DateInput
                  {...control}
                  value={to}
                  onValueChange={(value) => setFilters({ to: value ?? undefined })}
                />
              )}
            </FormField>
          </>
        }
        pagination={{
          hasPrevious: pages.hasPrevious,
          hasNext: list.data?.page.hasMore ?? false,
          onPrevious: pages.previous,
          onNext: () => pages.next(list.data?.page.nextCursor),
          pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
        }}
      />

      {search.tank ? (
        <CumulativeChart
          tankId={search.tank}
          label={
            tankItems.find((tank) => tank.tankId === search.tank)
              ? t("fuel.tanks.tankTitle", {
                  tankNo: tankItems.find((tank) => tank.tankId === search.tank)!.tankNo,
                  product: tankItems.find((tank) => tank.tankId === search.tank)!.productName,
                })
              : t("fuel.reconciliation.fields.tank")
          }
          range={{ from, to }}
        />
      ) : (
        <Alert tone="info">{t("fuel.reconciliation.selectTankForChart")}</Alert>
      )}

      <ReconciliationDrawer
        companyId={companyId}
        stationId={station.stationId}
        item={selected}
        onClose={() => setFilters({ row: undefined })}
      />
    </>
  );
}

/** Fark hücresi: RENK TEK BİLGİ TAŞIYICI DEĞİLDİR — yön ("Kayıp" / "Fazla") metinle de yazılır. */
function VarianceCell({ item }: { item: FuelReconciliation }): ReactElement {
  const { t } = useTranslation();
  const direction = varianceDirection(item.varianceL);
  const tone = direction === "loss" ? "danger" : direction === "surplus" ? "warning" : "neutral";
  return (
    <span className="inline-flex flex-wrap items-center justify-end gap-1">
      <QuantityText value={item.varianceL} unit="LITRE" />
      {item.variancePercent === null ? null : (
        <span className="text-xs text-muted-foreground tabular">
          %{Decimal.from(item.variancePercent).formatTr(2)}
        </span>
      )}
      {direction === "none" ? null : (
        <StatusBadge tone={tone}>{t(`fuel.reconciliation.direction.${direction}`)}</StatusBadge>
      )}
    </span>
  );
}

/** "Dünü hesapla": taslak mutabakat üretir. YAZMA işlemidir → `fuel.reconciliation.approve` + step-up. */
function ComputeDay({ stationId }: { stationId: string }): ReactElement | null {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshFuel();
  const withStepUp = useStepUp();
  const permissions = useFuelPermissions();
  const [businessDate, setBusinessDate] = useState<string>(() => addDays(toIstanbulDate(new Date()), -1));
  const [error, setError] = useState<string | null>(null);

  const compute = useMutation({
    mutationFn: async () =>
      (
        await withStepUp(() =>
          api.POST("/fuel/reconciliations/compute", { body: { stationId, businessDate } }),
        )
      ).data!,
    onSuccess: async (result) => {
      await refresh();
      toast({
        tone: "success",
        title: t("fuel.reconciliation.computed", {
          count: result.items.length,
          skipped: result.skipped.length,
        }),
      });
    },
    onError: (caught) =>
      setError(
        isApiError(caught, "PERIOD_NOT_CLOSED")
          ? t("fuel.reconciliation.periodNotClosed")
          : errorMessage(caught),
      ),
  });

  if (!permissions.canApproveReconciliation) return null;

  return (
    <Card className="grid gap-3 p-4">
      <h2 className="text-base font-semibold">{t("fuel.reconciliation.computeTitle")}</h2>
      <p className="text-sm text-muted-foreground">{t("fuel.reconciliation.computeHint")}</p>
      {error ? <Alert tone="danger">{error}</Alert> : null}
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          setError(null);
          compute.mutate();
        }}
      >
        <FormField label={t("fuel.reconciliation.computeDate")} className="w-44" required>
          {(control) => (
            <DateInput
              {...control}
              value={businessDate}
              onValueChange={(value) => value && setBusinessDate(value)}
            />
          )}
        </FormField>
        <Button type="submit" variant="outline" loading={compute.isPending}>
          <Calculator aria-hidden="true" />
          {t("fuel.reconciliation.compute")}
        </Button>
      </form>
    </Card>
  );
}

function CumulativeChart({
  tankId,
  label,
  range,
}: {
  tankId: string;
  label: string;
  range: { from: string; to: string };
}): ReactElement {
  const { t } = useTranslation();
  const cumulative = useCumulativeVariance(tankId, range);
  const points = useMemo(() => cumulative.data?.points ?? [], [cumulative.data]);
  const series = useMemo(() => cumulativeSeries(points), [points]);
  const option = useMemo(
    () =>
      cumulativeVarianceChartOption(series, {
        daily: t("fuel.reconciliation.chart.daily"),
        cumulative: t("fuel.reconciliation.chart.cumulative"),
        litresAxis: t("fuel.series.volumeAxis"),
      }),
    [series, t],
  );
  const last = lastCumulative(points);

  return (
    <Section title={t("fuel.reconciliation.chart.title", { tank: label })}>
      {cumulative.error ? (
        <Alert tone="danger">{errorMessage(cumulative.error)}</Alert>
      ) : !cumulative.data ? (
        <SkeletonRows rows={4} />
      ) : points.length === 0 ? (
        <EmptyState
          headingLevel={3}
          title={t("fuel.reconciliation.chart.empty")}
          description={t("fuel.reconciliation.chart.emptyHint")}
        />
      ) : (
        <>
          <p className="text-sm">
            {t("fuel.reconciliation.chart.summary", {
              value: `${last!.formatTr(3)} L`,
            })}
          </p>
          <ChartWithTable
            ariaLabel={t("fuel.reconciliation.chart.ariaLabel", { tank: label })}
            option={option}
            height={320}
            tableLabel={t("fuel.reconciliation.chart.tableLabel")}
          >
            <table className="w-full text-sm">
              <caption className="sr-only">{t("fuel.reconciliation.chart.tableLabel")}</caption>
              <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
                <tr>
                  <Th>{t("fuel.reconciliation.fields.day")}</Th>
                  <Th align="right">{t("fuel.reconciliation.chart.daily")}</Th>
                  <Th align="right">{t("fuel.reconciliation.chart.cumulative")}</Th>
                  <Th align="right">{t("fuel.reconciliation.fields.totalizerSales")}</Th>
                  <Th>{t("fuel.reconciliation.fields.classification")}</Th>
                </tr>
              </thead>
              <tbody>
                {points.map((point) => (
                  <tr key={point.businessDate} className="border-t">
                    <td className="px-3 py-2">{formatDate(point.businessDate)}</td>
                    <td className="px-3 py-2 text-right">
                      <QuantityText value={point.varianceL} unit="LITRE" />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <QuantityText value={point.cumulativeVarianceL} unit="LITRE" />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <QuantityText value={point.salesL} unit="LITRE" />
                    </td>
                    <td className="px-3 py-2">
                      <StatusBadge tone={CLASSIFICATION_TONE[point.classification]}>
                        {RECONCILIATION_CLASSIFICATION_LABELS[point.classification]}
                      </StatusBadge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ChartWithTable>
        </>
      )}
    </Section>
  );
}

function ReconciliationDrawer({
  companyId,
  stationId,
  item,
  onClose,
}: {
  companyId: string;
  stationId: string;
  item: FuelReconciliation | null;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshFuel();
  const withStepUp = useStepUp();
  const permissions = useFuelPermissions();
  const settings = useFuelSettings(companyId, stationId);
  const [explanation, setExplanation] = useState("");
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const approve = useMutation({
    mutationFn: async () =>
      (
        await withStepUp(() =>
          api.POST("/fuel/reconciliations/{id}/approve", {
            params: { path: { id: item!.id } },
            body: {
              version: item!.version,
              ...(explanation.trim() === "" ? {} : { explanation: explanation.trim() }),
            },
          }),
        )
      ).data!,
    onSuccess: async () => {
      setExplanation("");
      await refresh();
      toast({ tone: "success", title: t("fuel.reconciliation.approved") });
      onClose();
    },
    onError: (caught) => {
      // Sunucu "açıklama zorunlu" derse ileti ALANIN ALTINDA görünsün (genel uyarı kutusunda değil).
      if (isApiError(caught, "VALIDATION_ERROR")) {
        const field = (caught.problem.errors ?? []).find((entry) => entry.path.endsWith("explanation"));
        if (field) {
          setFieldError(field.message);
          return;
        }
      }
      setError(errorMessage(caught));
    },
  });

  const formula = item ? reconciliationFormula(item) : null;
  const minimumL = settings.data?.reconciliationThresholds.dailyVariance.minimumL ?? null;
  const explanationNeeded = item ? explanationRequired(item.varianceL, minimumL) : false;

  return (
    <Drawer
      open={item !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={
        item
          ? t("fuel.reconciliation.drawer.title", {
              date: formatDate(item.businessDate),
              tankNo: item.tankNo,
              product: item.productName,
            })
          : t("fuel.reconciliation.drawer.empty")
      }
    >
      {item === null || formula === null ? null : (
        <div className="grid gap-4">
          <div className="flex flex-wrap gap-2">
            <StatusBadge tone={CLASSIFICATION_TONE[item.classification]}>
              {RECONCILIATION_CLASSIFICATION_LABELS[item.classification]}
            </StatusBadge>
            <StatusBadge tone={RECONCILIATION_STATUS_TONE[item.status]}>
              {RECONCILIATION_STATUS_LABELS[item.status]}
            </StatusBadge>
          </div>

          {formula.consistent ? null : (
            <Alert tone="warning">{t("fuel.reconciliation.drawer.inconsistent")}</Alert>
          )}

          <section className="grid gap-2">
            <h3 className="text-sm font-semibold">{t("fuel.reconciliation.drawer.formula")}</h3>
            <p className="text-xs text-muted-foreground">{t("fuel.reconciliation.drawer.formulaHint")}</p>
            <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
              <Field label={t("fuel.reconciliation.fields.opening")}>
                {`${formula.openingL.formatTr(3)} L`}
              </Field>
              <Field label={t("fuel.reconciliation.drawer.plusDelivered")}>
                {`${formula.deliveredL.formatTr(3)} L`}
              </Field>
              <Field label={t("fuel.reconciliation.drawer.minusSales")}>
                {`${formula.totalizerSalesL.formatTr(3)} L`}
              </Field>
              <Field label={t("fuel.reconciliation.drawer.plusTestReturned")}>
                {`${formula.testReturnedL.formatTr(3)} L`}
              </Field>
              <Field label={t("fuel.reconciliation.fields.book")}>
                {`${formula.reportedBookL.formatTr(3)} L`}
              </Field>
              <Field label={t("fuel.reconciliation.fields.closing")}>
                {`${formula.closingL.formatTr(3)} L`}
              </Field>
              <Field label={t("fuel.reconciliation.fields.variance")}>
                {`${formula.reportedVarianceL.formatTr(3)} L`}
              </Field>
              <Field label={t("fuel.reconciliation.fields.salesCheck")}>
                {`${Decimal.from(item.salesCheckDifferenceL).formatTr(3)} L`}
              </Field>
            </dl>
            <p className="text-xs text-muted-foreground">{t("fuel.reconciliation.drawer.salesCheckHint")}</p>
          </section>

          <section className="grid gap-2">
            <h3 className="text-sm font-semibold">{t("fuel.reconciliation.drawer.findings")}</h3>
            {item.findings.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("fuel.reconciliation.drawer.noFindings")}</p>
            ) : (
              <ul className="grid gap-2">
                {item.findings.map((finding) => (
                  <li key={finding.rule} className="rounded-md border p-3 text-sm">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{RECONCILIATION_RULE_LABELS[finding.rule]}</span>
                      <StatusBadge tone={FINDING_SEVERITY_TONE[finding.severity]}>
                        {RECONCILIATION_CLASSIFICATION_LABELS[finding.severity]}
                      </StatusBadge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {t("fuel.reconciliation.drawer.findingValues", {
                        measured: finding.measured,
                        threshold: finding.threshold,
                      })}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {item.dataQuality.length > 0 ? (
            <section className="grid gap-2">
              <h3 className="text-sm font-semibold">{t("fuel.reconciliation.drawer.dataQuality")}</h3>
              <ul className="grid gap-1 text-sm">
                {item.dataQuality.map((entry) => (
                  <li key={entry.code}>
                    {RECONCILIATION_DATA_QUALITY_LABELS[entry.code]}
                    {entry.detail ? ` — ${entry.detail}` : ""}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {item.status === "APPROVED" && hasLossBreakdown(item) ? (
            <section className="grid gap-2">
              <h3 className="text-sm font-semibold">{t("fuel.reconciliation.drawer.loss")}</h3>
              <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
                <Field label={t("fuel.reconciliation.drawer.normalLoss")}>
                  <QuantityText value={item.normalLossL} unit="LITRE" />
                </Field>
                <Field label={t("fuel.reconciliation.drawer.abnormalLoss")}>
                  <QuantityText value={item.abnormalLossL} unit="LITRE" />
                </Field>
                <Field label={t("fuel.reconciliation.drawer.surplus")}>
                  <QuantityText value={item.surplusL} unit="LITRE" />
                </Field>
                <Field label={t("fuel.reconciliation.drawer.unitCost")}>
                  {item.lossUnitCost === null ? "—" : `${Decimal.from(item.lossUnitCost).formatTr(4)} ₺/L`}
                </Field>
                <Field label={t("fuel.reconciliation.drawer.lossAmount")}>
                  {item.lossAmount === null ? "—" : Money.fromMinor(item.lossAmount).formatTr()}
                </Field>
                <Field label={t("fuel.reconciliation.drawer.ledger")}>
                  {FUEL_LEDGER_STATUS_LABELS[item.ledgerStatus]}
                </Field>
              </dl>
              {item.journalEntryId ? (
                <Link
                  to="/muhasebe/fisler/$id"
                  params={{ id: item.journalEntryId }}
                  className="text-sm underline underline-offset-4"
                >
                  {t("fuel.reconciliation.drawer.openEntry")}
                </Link>
              ) : null}
            </section>
          ) : null}

          {item.explanation ? (
            <section className="grid gap-1">
              <h3 className="text-sm font-semibold">{t("fuel.reconciliation.drawer.explanation")}</h3>
              <p className="text-sm">{item.explanation}</p>
            </section>
          ) : null}

          {item.status === "DRAFT" && permissions.canApproveReconciliation ? (
            <form
              className="grid gap-3 border-t pt-4"
              onSubmit={(event) => {
                event.preventDefault();
                setError(null);
                setFieldError(null);
                approve.mutate();
              }}
            >
              {error ? <Alert tone="danger">{error}</Alert> : null}
              <FormField
                label={t("fuel.reconciliation.drawer.explanation")}
                description={t("fuel.reconciliation.drawer.explanationHint")}
                required={explanationNeeded}
                {...(fieldError ? { error: fieldError } : {})}
              >
                {(control) => (
                  <Textarea
                    {...control}
                    rows={3}
                    maxLength={1000}
                    value={explanation}
                    onChange={(event) => setExplanation(event.target.value)}
                  />
                )}
              </FormField>
              <Button type="submit" loading={approve.isPending} className="justify-self-start">
                {t("fuel.reconciliation.drawer.approve")}
              </Button>
            </form>
          ) : null}
        </div>
      )}
    </Drawer>
  );
}
